import * as React from 'react';

import { Flex } from 'react-system';

import { useLocale } from '@legacy/hooks/locale';
import { useTheme } from '@legacy/hooks/theme';

type Props = {
  length: number;
  onChange: (value: string) => void;
  disabled?: boolean;
  error?: boolean;
  // Names the group of boxes for assistive tech. Without it a screen reader
  // announces N consecutive unlabelled text fields with nothing to say what
  // they are for; the surrounding heading is not associated with them.
  label?: string;
};

type State = {
  values: string[];
};

type ItemProps = {
  autoFocus: boolean;
  position: number;
  total: number;
  disabled: boolean;
  error: boolean;
  value: string;
  onChange: (value: string) => void;
  onBackspace: () => void;
};

const Item = React.forwardRef<HTMLInputElement, ItemProps>((props, ref) => {
  const { t } = useLocale();
  const { borderRadius, text, colors } = useTheme();
  const borderColor = props.error ? colors.errorText : colors.grey500;
  return (
    <input
      ref={ref}
      css={{
        margin: '0 3px',
        textAlign: 'center',
        background: 'transparent',
        color: colors.black,
        width: 44,
        height: 52,
        borderRadius: borderRadius.small,
        border: `1px solid ${borderColor}`,
        boxSizing: 'border-box',
        fontWeight: text.font.bold,
        fontSize: '1.4em',
        padding: 5,
        transition: 'border-color 120ms, box-shadow 120ms',

        ':focus': {
          outline: 'none',
          border: `1px solid ${colors.primaryMain}`,
          boxShadow: `0 0 0 3px ${colors.primaryMain}33`,
        },
        ':disabled': {
          opacity: 0.6,
        },
      }}
      aria-label={t('Digit {{position}} of {{total}}', {
        position: String(props.position),
        total: String(props.total),
      })}
      maxLength={1}
      // Lets iOS/Android offer the code straight from the email or SMS. Set on
      // every box because the platform fills whichever one has focus.
      autoComplete="one-time-code"
      inputMode="numeric"
      type="tel"
      disabled={props.disabled}
      autoFocus={props.autoFocus}
      value={props.value}
      onChange={e => {
        // Keep digits only. This can arrive longer than one character when the
        // platform autofills the whole code into the focused box; the parent
        // spreads it across the remaining boxes.
        props.onChange(e.target.value.replace(/\D/g, ''));
      }}
      onKeyDown={e => {
        if (e.keyCode === 8 && props.value.length === 0) {
          props.onBackspace();
        }
      }}
    />
  );
});

export class PinInput extends React.Component<Props, State> {
  state: State = {
    values: Array.from<void, string>(Array(this.props.length), () => ''),
  };
  inputRefs: Array<any> = this.state.values.map<any>(() => React.createRef());

  // Writes `digits` starting at `startIndex`, moves focus to the first box
  // still empty, and notifies the parent once every box is filled. Shared by
  // typing, autofill and paste so all three behave the same.
  fill(startIndex: number, digits: string) {
    this.setState(
      prev => ({
        values: prev.values.map((existing, index) => {
          if (index < startIndex) {
            return existing;
          }
          const offset = index - startIndex;
          if (offset < digits.length) {
            return digits[offset];
          }
          // Only the edited box is cleared. Writing '' to the rest would wipe
          // the digits after it on every single-character edit — retyping one
          // box in the middle would blank the tail.
          return offset === 0 ? '' : existing;
        }),
      }),
      () => {
        const { values } = this.state;
        const firstEmpty = values.findIndex(value => value === '');
        const focusIndex =
          firstEmpty === -1 ? this.props.length - 1 : firstEmpty;
        this.inputRefs[focusIndex]?.current?.focus();

        const pin = values.join('');
        if (pin.length === this.props.length) {
          this.props.onChange(pin);
        }
      },
    );
  }

  render() {
    const { disabled = false, error = false } = this.props;
    return (
      <Flex
        role="group"
        aria-label={this.props.label}
        justifyContent="center"
        // Paste is handled here rather than per box: maxLength truncates a
        // pasted code to one character, so without this a copied code from the
        // email cannot be pasted at all.
        onPaste={(event: React.ClipboardEvent<HTMLDivElement>) => {
          const digits = event.clipboardData
            .getData('text')
            .replace(/\D/g, '')
            .slice(0, this.props.length);
          if (digits.length === 0) {
            return;
          }
          event.preventDefault();
          this.fill(0, digits);
        }}
      >
        {this.state.values.map((value, index) => (
          <Item
            key={index}
            ref={this.inputRefs[index]}
            position={index + 1}
            total={this.props.length}
            value={value}
            disabled={disabled}
            error={error}
            autoFocus={index === 0}
            onChange={digits => {
              if (digits === value) {
                return;
              }
              // one-time-code autofill lands in whichever box has focus, so a
              // whole code can arrive at a box other than the first. Anchor a
              // full-length value at 0; otherwise it would be truncated and,
              // if the earlier boxes were already filled, auto-submitted —
              // spending one of the five attempts the code now gets.
              this.fill(digits.length >= this.props.length ? 0 : index, digits);
            }}
            onBackspace={() => {
              if (index !== 0) {
                const { current } = this.inputRefs[index - 1];
                if (current) {
                  current.focus();
                }
              }
            }}
          />
        ))}
      </Flex>
    );
  }
}
